import moment from "moment";

// Quantity maths shared by the Stock Transfer screen's "Split Equally" button
// and the multi-store submit that actually sends the split.

// Divide `total` into `storeCount` whole-number shares that add back up to
// exactly `total` — no unit is lost to rounding. The remainder is handed to
// the LAST stores, so the shares only ever rise across the list: 43 over 2
// stores is [21, 22], 43 over 3 is [14, 14, 15].
//
// Quantities here are whole units (the batch quantity inputs are integers), so
// a fractional total is floored before splitting rather than spread as
// fractions across stores.
export const splitQuantityAcrossStores = (total, storeCount) => {
  const count = Math.max(0, Math.floor(Number(storeCount) || 0));
  if (count === 0) return [];
  const amount = Math.max(0, Math.floor(Number(total) || 0));
  const base = Math.floor(amount / count);
  // How many stores get one extra unit — always fewer than `count`, so the
  // biggest share is never more than one unit above the smallest.
  const remainder = amount - base * count;
  return Array.from(
    { length: count },
    (_, index) => base + (index >= count - remainder ? 1 : 0)
  );
};

// The share store `storeIndex` of `storeCount` receives out of `total`.
export const getStoreShare = (total, storeCount, storeIndex) =>
  splitQuantityAcrossStores(total, storeCount)[storeIndex] ?? 0;

const toAmount = (value) => +(value || 0).toFixed(2);

// Re-express batches loaded from the expiry API in a UOM holding `baseQuantity`
// units. The API gives stock, purchase rate and MRP per single unit, and those
// raw values stay on the batch so the row can switch UOM without drifting.
export const convertBatchesToUom = (batches = [], baseQuantity = 1) => {
  const factor = parseFloat(baseQuantity) || 1;
  return batches.map((batch) =>
    batch?.unit_batch_quantity === undefined
      ? batch
      : {
          ...batch,
          item_batch_quantity: batch.unit_batch_quantity / factor,
          item_batch_purchase_rate: toAmount(batch.unit_purchase_rate * factor),
          item_batch_unit_price: toAmount(batch.unit_mrp * factor),
        }
  );
};

// Turn the expiry API rows (one per item / batch / bin) into stock transfer
// grid rows, one per item. The item opens in its pricing UOM when every batch
// fills whole packs of it, otherwise in its smallest UOM so batch quantities
// stay whole numbers. For In Store Transfer, `toBinId` is where the stock is
// going — batches already sitting in that bin are left out.
export const buildExpiredItemRows = (
  rows = [],
  { toBinId = "", toBinLocation = "" } = {}
) => {
  const rowsByItem = new Map();
  rows.forEach((row) => {
    if (toBinId && row?.bin_no_id === toBinId) return;
    if (!rowsByItem.has(row?.item_id)) rowsByItem.set(row?.item_id, []);
    rowsByItem.get(row?.item_id).push(row);
  });

  return [...rowsByItem.values()].map((itemRows) => {
    const item = itemRows[0];
    const uomList = item?.uom_dropdown || [];
    const smallestUom = [...uomList].sort(
      (a, b) => (parseFloat(a?.base_quantity) || 0) - (parseFloat(b?.base_quantity) || 0)
    )[0];
    const pricingUom = uomList.find(
      (uom) => uom?.item_id === item?.item_pricing_uom_id
    );
    const pricingBaseQty = parseFloat(pricingUom?.base_quantity) || 0;
    const fillsPricingUom =
      pricingBaseQty > 0 &&
      itemRows.every(
        (row) => (parseFloat(row?.item_quantity) || 0) % pricingBaseQty === 0
      );
    const selectedUom = (fillsPricingUom ? pricingUom : smallestUom) ?? pricingUom;
    const baseQuantity = parseFloat(selectedUom?.base_quantity) || 1;

    const batches = convertBatchesToUom(
      itemRows.map((row) => ({
        item_id: row?.item_id,
        item_batch_number: row?.item_batch_number,
        unit_batch_quantity: parseFloat(row?.item_quantity) || 0,
        unit_purchase_rate: parseFloat(row?.final_pr) || 0,
        unit_mrp: parseFloat(row?.mrp) || 0,
        item_batch_free_quantity: 0,
        item_batch_discount_percentage: 0,
        item_batch_discount_amount: 0,
        from_bin_id: row?.bin_no_id ?? "",
        from_bin_location: row?.bin_location_no ?? "",
        to_bin_id: toBinId,
        to_bin_location: toBinLocation,
        item_exp_date: row?.item_exp_date
          ? moment(row.item_exp_date).format("YYYY-MM-DD")
          : "",
        created_date: "",
        remainingQty: 0,
      })),
      baseQuantity
    );
    const totalUnits = batches.reduce(
      (sum, batch) => sum + batch.unit_batch_quantity,
      0
    );

    return {
      sl_no: 1,
      item_name: item?.item_name || item?.item_code || "",
      item_code: item?.item_code || "",
      item_uom: selectedUom?.alt_uom || "",
      quantity: totalUnits / baseQuantity,
      item_batches: batches,
      current_avail: {
        item_uom_source: "",
        item_uom_destination: "",
      },
      item_id: item?.item_id || "",
      item_uom_id: selectedUom?.item_id || "",
      base_quantity: baseQuantity,
      uom_dropdown: uomList.map((uom) => ({
        item_uom_id: uom?.item_id,
        label: uom?.alt_uom,
        value: uom?.alt_uom,
        base_quantity: uom?.base_quantity,
      })),
      total_sellable_quantity: totalUnits / baseQuantity,
      base_total_sellable_quantity: totalUnits,
      from_warehouse_id: "",
      from_warehouse_name: "",
      to_warehouse_id: "",
      to_warehouse_name: "",
    };
  });
};
